<template>
  <component
    :is="rootTag"
    v-bind="{ ...$attrs, ...rootBind }"
    :class="rootClass"
  >
    <!-- spinner inherits variant text color via currentColor -->
    <Loader2 v-if="loading" class="animate-spin" :class="iconSizeClass" />
    <component :is="icon" v-else-if="icon && !isIconVariant" :class="iconSizeClass" />
    <span v-if="loading && loadingText">{{ loadingText }}</span>
    <slot v-else />
    <component :is="iconRight" v-if="iconRight && !isIconVariant && !loading" :class="iconSizeClass" />
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, resolveComponent, type Component } from 'vue'
import { Loader2 } from 'lucide-vue-next'

defineOptions({ inheritAttrs: false })

// Map kept as literal `btn-*` strings (NOT a runtime `btn-${variant}` template)
// so Tailwind's content scanner sees every class and never purges them.
// Hoisted to module scope: built once, not on every rootClass recompute.
const VARIANT_CLASS: Record<string, string> = {
  primary:   'btn-primary',
  secondary: 'btn-secondary',
  warning:   'btn-warning',
  danger:    'btn-danger',
  ghost:     'btn-ghost',
}

const props = withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'warning' | 'danger' | 'ghost' | 'icon' | 'overlay-icon' | 'portal-icon'
  size?: 'md' | 'sm'
  tone?: 'default' | 'danger' | 'acc'
  icon?: Component
  iconRight?: Component
  loading?: boolean
  loadingText?: string
  tip?: string
  tipSide?: 'center' | 'left'
  label?: string
  type?: 'button' | 'submit' | 'reset'
  to?: string | object
  href?: string
  disabled?: boolean
}>(), {
  variant: 'secondary',
  size: 'md',
  tone: 'default',
  tipSide: 'center',
  type: 'button',
})

const isIconVariant = computed(() =>
  props.variant === 'icon' || props.variant === 'overlay-icon' || props.variant === 'portal-icon')

const NuxtLink = resolveComponent('NuxtLink')
const rootTag = computed(() => (props.to ? NuxtLink : props.href ? 'a' : 'button'))

const iconSizeClass = computed(() => (props.size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'))

const hasGap = computed(() => {
  if (isIconVariant.value) return false
  const hasLeading = props.loading || !!props.icon
  return hasLeading || !!props.iconRight
})

const rootClass = computed(() => {
  const c: string[] = []
  if (props.variant === 'icon') {
    c.push('icon-btn')
    if (props.tone === 'danger') c.push('danger')
    if (props.tipSide === 'left') c.push('tooltip-left')
  } else if (props.variant === 'overlay-icon') {
    c.push('icon-btn')
    if (props.tone === 'acc') c.push('icon-btn--acc')
    else if (props.tone === 'danger') c.push('icon-btn--danger')
  } else if (props.variant === 'portal-icon') {
    c.push('lp-icon-btn')
  } else {
    c.push(VARIANT_CLASS[props.variant] ?? 'btn-secondary')
    if (props.size === 'sm') c.push('btn-sm')
    if (hasGap.value) c.push(props.size === 'sm' ? 'gap-1.5' : 'gap-2')
  }
  // native `disabled` does not apply to <a>/NuxtLink — add visual + interaction guard
  if (rootTag.value !== 'button' && (props.disabled || props.loading)) {
    c.push('pointer-events-none', 'opacity-50')
  }
  return c
})

const rootBind = computed<Record<string, unknown>>(() => {
  const b: Record<string, unknown> = {}
  if (rootTag.value === 'button') {
    b.type = props.type
    b.disabled = props.disabled || props.loading || undefined
  } else if (rootTag.value === 'a') {
    // native `disabled` does not apply to <a> — use aria-disabled + tabindex instead
    b.href = props.href
    b.target = '_blank'
    b.rel = 'noopener noreferrer'
    if (props.disabled || props.loading) {
      b['aria-disabled'] = true
      b.tabindex = -1
    }
  } else {
    // NuxtLink branch — native `disabled` does not apply to <a> rendered by NuxtLink
    b.to = props.to
    if (props.disabled || props.loading) {
      b['aria-disabled'] = true
      b.tabindex = -1
    }
  }
  if (props.tip) b['data-tip'] = props.tip
  const aria = props.label || props.tip
  if (aria) b['aria-label'] = aria
  return b
})

onMounted(() => {
  if (isIconVariant.value && !props.tip && !props.label) {
    console.warn('[AppButton] icon-only button needs a `tip` or `label` for accessibility.')
  }
  if ((props.disabled || props.loading) && rootTag.value !== 'button') {
    console.warn('[AppButton] disabled/loading on a link (to/href) is not natively enforced; aria-disabled + pointer-events applied.')
  }
})
</script>
